import { type FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

export interface TabOption {
  id: string
  label: string
}

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

export interface TabsProps {
  options: readonly TabOption[]
  value?: string
  defaultValue?: string
  onChange?: (value: string) => void
  className?: string
}

export const Tabs: FC<TabsProps> = ({ options, value, defaultValue, onChange, className }) => {
  const isControlled = value !== undefined
  const firstId = useMemo(() => options[0]?.id, [options])
  const [internalValue, setInternalValue] = useState(() => value ?? defaultValue ?? firstId)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())
  const [highlight, setHighlight] = useState<{ width: number; left: number; visible: boolean }>(
    () => ({ width: 0, left: 0, visible: false })
  )

  useEffect(() => {
    if (isControlled) {
      setInternalValue(value ?? firstId)
    } else if (!internalValue && firstId) {
      setInternalValue(firstId)
    }
  }, [firstId, internalValue, isControlled, value])

  useEffect(() => {
    if (!isControlled && !internalValue && firstId) {
      setInternalValue(firstId)
    }
  }, [firstId, internalValue, isControlled])

  const updateHighlight = useCallback(() => {
    const activeKey = isControlled ? value : internalValue
    const container = containerRef.current
    if (!container || !activeKey) {
      setHighlight((prev) => (prev.visible ? { width: 0, left: 0, visible: false } : prev))
      return
    }
    const activeButton = buttonRefs.current.get(activeKey)
    if (!activeButton) {
      setHighlight((prev) => (prev.visible ? { width: 0, left: 0, visible: false } : prev))
      return
    }
    const containerRect = container.getBoundingClientRect()
    const activeRect = activeButton.getBoundingClientRect()
    setHighlight({
      width: activeRect.width,
      left: activeRect.left - containerRect.left,
      visible: true,
    })
  }, [internalValue, isControlled, value])

  useLayoutEffect(() => {
    updateHighlight()
  }, [updateHighlight])

  useEffect(() => {
    const handleResize = () => updateHighlight()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [updateHighlight])

  const handleSelect = (id: string) => {
    if (!isControlled) {
      setInternalValue(id)
    }
    onChange?.(id)
  }

  const setButtonRef = useCallback(
    (id: string, node: HTMLButtonElement | null) => {
      if (node) {
        buttonRefs.current.set(id, node)
      } else {
        buttonRefs.current.delete(id)
      }
      requestAnimationFrame(() => updateHighlight())
    },
    [updateHighlight]
  )

  const activeId = isControlled ? value : internalValue

  return (
    <div
      ref={containerRef}
      className={cx(
        'relative flex items-center gap-6 rounded-[40px] bg-[rgba(24,24,27,0.02)] p-0.5',
        className
      )}
    >
      {highlight.visible ? (
        <div
          className="pointer-events-none absolute top-0.5 bottom-0.5 rounded-[36px] bg-white shadow-[0_0_0_1px_rgba(24,24,27,0.06)] ring-1 ring-[rgba(24,24,27,0.06)] transition-all duration-200 ease-out"
          style={{ width: highlight.width, transform: `translateX(${highlight.left}px)` }}
        />
      ) : null}
      {options.map((option) => {
        const isActive = option.id === activeId
        return (
          <button
            key={option.id}
            type="button"
            onClick={() => handleSelect(option.id)}
            ref={(node) => setButtonRef(option.id, node)}
            className={cx(
              'relative z-10 flex-1 rounded-[36px] px-3 py-1.5 text-[14px] font-medium tracking-[-0.41px] transition-colors duration-200 ease-out',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1f1f1f]',
              isActive ? 'text-[#1f1f1f]' : 'text-[#71717a]'
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

export default Tabs
